import { useState } from 'react'
import { REGISTRY_ID, publishedReports } from '../domain/catalog'
import { parseRepositoryRef } from '../domain/repository'
import type { CatalogState } from '../state/catalogState'
import { PREFERENCE, readPreference, writePreference } from '../state/prefs'
import type { ReportsState } from '../state/reports'
import { Button } from '../ui/Button'
import { Icon } from '../ui/Icon'

type Shown = { status: 'idle' } | { status: 'loading' } | { status: 'done'; message: string }

/**
 * Offers the EVERSE Assessment Registry, which ships switched off: following
 * it is a choice to fetch from a third party on every sync, so it is asked,
 * not assumed. Following and looking once are separate choices, since some
 * want to look once and others to keep up to date.
 */
export function RegistryPrompt({ state, catalog }: { state: ReportsState; catalog: CatalogState }) {
  const [dismissed, setDismissed] = useState(
    () => readPreference(PREFERENCE.registryPromptDismissed) === 'true',
  )
  const [shown, setShown] = useState<Shown>({ status: 'idle' })
  const source = catalog.sources.find((s) => s.id === REGISTRY_ID)
  const following = source?.enabled !== false
  // Once followed, there is nothing left to suggest; Sources is where to stop.
  if (dismissed || !source?.repository || following) return null

  const showOnce = async () => {
    const ref = parseRepositoryRef(source.repository!)
    if (!ref) return
    setShown({ status: 'loading' })
    const found = await publishedReports(ref)
    if (found.error || found.entries.length === 0) {
      setShown({
        status: 'done',
        message:
          'The registry has not published any assessments yet. Following it brings them in as soon as it does.',
      })
      return
    }
    await state.addFromUrls(found.entries.map((entry) => entry.reportUrl))
    setShown({
      status: 'done',
      message: `Loaded ${found.entries.length} ${found.entries.length === 1 ? 'assessment' : 'assessments'} from the registry.`,
    })
  }

  return (
    <section className="registry-prompt" aria-labelledby="registry-prompt-title">
      <div className="registry-prompt-head">
        <span className="icon-badge" aria-hidden="true">
          <Icon name="globe" size={20} />
        </span>
        <div>
          <h2 id="registry-prompt-title" className="card-title">
            {source.name}
          </h2>
          <p className="card-desc m-0">
            Assessments EVERSE publishes at{' '}
            <a href={`https://github.com/${source.repository}`} target="_blank" rel="noreferrer">
              {source.repository}
            </a>
            . Follow it to load new ones whenever the sources sync, or show what it has now, once.
          </p>
          <p className="small m-0 mt-1" role="status">
            {shown.status === 'done' ? shown.message : ''}
          </p>
        </div>
      </div>
      <div className="row mt-2">
        <Button
          tone="primary"
          size="small"
          // A plus: it adds the registry as a source. A tick would read as done.
          icon="plus"
          label="Follow the registry"
          onClick={() => catalog.toggleSource(REGISTRY_ID, true)}
        />
        <Button
          size="small"
          icon="download"
          label={shown.status === 'loading' ? 'Loading...' : 'Show assessments'}
          disabled={shown.status === 'loading' || state.busy}
          onClick={() => void showOnce()}
        />
        <Button
          tone="quiet"
          size="small"
          label="Do not ask again"
          onClick={() => {
            writePreference(PREFERENCE.registryPromptDismissed, 'true')
            setDismissed(true)
          }}
        />
      </div>
    </section>
  )
}
